/**
 * useFocusTrap — trap Tab focus within a modal dialog and restore focus
 * on close (WAI-ARIA APG dialog pattern).
 *
 * When active, intercepts Tab and Shift+Tab to keep focus cycling within
 * the container's focusable descendants. On deactivation, restores focus to
 * the element that had focus before the trap was activated.
 *
 * Complements existing Escape and backdrop-click handlers — this hook only
 * owns Tab wrapping.
 *
 * Usage:
 *   const panelRef = useRef<HTMLDivElement>(null);
 *   useFocusTrap(panelRef, isDialogOpen);
 */
import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  active: boolean,
): void {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !active) return;

    // Remember the element that had focus before the trap was activated.
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;

      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled'));

      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement;

      if (e.shiftKey && activeEl === first) {
        // Shift+Tab on first focusable → wrap to last.
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        // Tab on last focusable → wrap to first.
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);

      // Restore focus to the element that had focus before the trap was activated.
      const previous = previousFocusRef.current;
      if (previous && previous.isConnected) {
        previous.focus();
      }
    };
  }, [containerRef, active]);
}
